import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  priorityTagRecipe,
  weightClassBarRecipe,
  weightClassRecipe,
} from '../recipes/list.recipe';
import { Inline, Stack } from './Layout';
import { callStatusLabel } from './call-status';
import { ListRow, type ListRowProps } from './List';
import { StatusChip } from './StatusChip';
import { toneForStatus } from './status-tone';
import { Text } from './Text';

type Density = 'comfortable' | 'compact';

export interface RecordRowProps
  extends Omit<ListRowProps, 'title' | 'children' | 'as'> {
  /** What the record is, in the words a person looks for: an address, a car. */
  title: React.ReactNode;
  /** The line under it: numbers, a plate, a customer. */
  meta?: React.ReactNode;
  /** A third line, left out in `compact`: a lot location, a date. */
  detail?: React.ReactNode;
  /** Above the title, on a line of its own: a priority tag. */
  badge?: React.ReactNode;
  /** Top right: the status. */
  status?: React.ReactNode;
  /** Bottom right, level with the last line: a total, a time, days at the lot. */
  figure?: React.ReactNode;
  /** `compact` drops the third line and tightens the row, for a long list. */
  density?: Density;
}

/**
 * One record in a phone list — a call, an impound, an invoice. What it is and
 * the line under it on the left; its status top right and a figure bottom
 * right, level with the last line.
 *
 *   <Stack divided gap={0}>
 *     <RecordRow title="2019 Ford F-150" meta="8ABC123 · IMP-0042"
 *       detail="Front lot, row C" status={<Badge tone="warning">At lot</Badge>}
 *       figure="12 days" onClick={open} />
 *   </Stack>
 *
 * Drawn to the Figma's calls list: no card, a rule between rows with 16px
 * either side, the title at the large body size and the line under it in
 * the same size, muted. A button when it has an onClick. CallListRow is
 * this with a call's own fields; reach for it, and for this only for a list
 * that has no row of its own. Impounds, invoices and inspection reports are
 * the grouped list instead: ListColumnRow.
 */
export const RecordRow = React.forwardRef<HTMLElement, RecordRowProps>(
  function RecordRow(
    {
      title,
      meta,
      detail,
      badge,
      status,
      figure,
      density = 'comfortable',
      onClick,
      ...props
    },
    ref
  ) {
    const compact = density === 'compact';
    return (
      <ListRow
        ref={ref}
        as={onClick ? 'button' : 'div'}
        interactive={Boolean(onClick)}
        inset={compact ? 'sm' : 'ruled'}
        onClick={onClick}
        {...props}
      >
        <Stack gap={2} grow align="start">
          {badge}
          <Stack gap={1}>
            <Text as="span" variant="body-lg-strong" truncate>
              {title}
            </Text>
            {meta ? (
              <Text as="span" variant="body-lg" tone="muted" truncate>
                {meta}
              </Text>
            ) : null}
            {detail && !compact ? (
              <Text as="span" variant="body" tone="muted" truncate>
                {detail}
              </Text>
            ) : null}
          </Stack>
        </Stack>
        {status || figure ? (
          <Stack
            gap={2}
            align="end"
            justify="between"
            className="shrink-0 self-stretch"
          >
            {status}
            {figure ? (
              <Text as="span" variant="body" tone="muted" numeric>
                {figure}
              </Text>
            ) : null}
          </Stack>
        ) : null}
      </ListRow>
    );
  }
);

export type WeightClass = 'light' | 'medium' | 'heavy';

const WEIGHT_STEPS: Record<WeightClass, number> = {
  light: 1,
  medium: 2,
  heavy: 3,
};

const WEIGHT_LABEL: Record<WeightClass, string> = {
  light: 'Light duty',
  medium: 'Medium duty',
  heavy: 'Heavy duty',
};

/** The word beside the bars in a row, as the Figma writes it. */
const WEIGHT_WORD: Record<WeightClass, string> = {
  light: 'Light',
  medium: 'Medium',
  heavy: 'Heavy',
};

export interface WeightClassBarsProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  weightClass: WeightClass;
}

/**
 * Light, medium or heavy duty as three rising bars, as many filled as the
 * class is heavy. Named in words for a screen reader.
 */
export const WeightClassBars = React.forwardRef<
  HTMLSpanElement,
  WeightClassBarsProps
>(function WeightClassBars({ weightClass, className, ...props }, ref) {
  const filled = WEIGHT_STEPS[weightClass];
  return (
    <span
      ref={ref}
      role="img"
      aria-label={WEIGHT_LABEL[weightClass]}
      className={cn(weightClassRecipe(), className)}
      {...props}
    >
      {([1, 2, 3] as const).map((step) => (
        <span
          key={step}
          className={weightClassBarRecipe({ step, filled: step <= filled })}
        />
      ))}
    </span>
  );
});

export type CallPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

const PRIORITY: Record<
  CallPriority,
  { label: string; tone: 'neutral' | 'warning' | 'destructive' }
> = {
  LOW: { label: 'Low', tone: 'neutral' },
  NORMAL: { label: 'Normal', tone: 'neutral' },
  HIGH: { label: 'High', tone: 'warning' },
  URGENT: { label: 'Urgent', tone: 'destructive' },
};

export interface PriorityTagProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  priority: CallPriority;
}

/**
 * A call's priority as a small square tag: quiet grey for normal and low,
 * amber for high, red for urgent. The calls list draws it above each
 * address; a call's card draws it beside its number.
 *
 *   <PriorityTag priority="HIGH" />
 */
export const PriorityTag = React.forwardRef<HTMLSpanElement, PriorityTagProps>(
  function PriorityTag({ priority, className, ...props }, ref) {
    return (
      <span
        ref={ref}
        className={cn(
          priorityTagRecipe({ tone: PRIORITY[priority].tone }),
          className
        )}
        {...props}
      >
        {PRIORITY[priority].label}
      </span>
    );
  }
);

export interface CallListRowProps
  extends Omit<
    RecordRowProps,
    'title' | 'meta' | 'detail' | 'badge' | 'status' | 'figure'
  > {
  /** Where the call is: the first line of the pickup address. */
  address: string;
  /** The town it is in, under the address: "Huntington Beach". */
  locality?: React.ReactNode;
  /**
   * A status string from the API, coloured through statusTone and said in
   * callStatusLabel's words.
   */
  status: string;
  /** What the chip says, when not the status's own word. */
  statusLabel?: string;
  priority?: CallPriority;
  /** The call's number, muted beside the priority tag: "096" reads "#096". */
  number?: string;
  weightClass?: WeightClass;
}

/**
 * One call in the calls list, as the Figma's list draws it: the priority
 * tag and the call's number, then the street and the town, on the left; the
 * status chip top right and the weight class bottom right. The time it was
 * booked used to stand in for a missing weight class; the Figma has none,
 * and the number is what a driver and the office say to each other.
 *
 *   <CallListRow address="La Jolla Cir" locality="Huntington Beach"
 *     status="DISPATCHED" priority="HIGH" number="096" weightClass="heavy"
 *     onClick={() => open(call.id)} />
 *
 * Every row has a priority tag, as the Figma shows. Normal and low are a
 * faint grey so a list of them reads as quiet; high and urgent take colour,
 * which is what lets the eye find them.
 */
export const CallListRow = React.forwardRef<HTMLElement, CallListRowProps>(
  function CallListRow(
    {
      address,
      locality,
      status,
      statusLabel,
      priority,
      number,
      weightClass,
      ...props
    },
    ref
  ) {
    return (
      <RecordRow
        ref={ref}
        title={address}
        meta={locality}
        badge={
          priority || number ? (
            <Inline as="span" gap={2} align="center">
              {priority ? <PriorityTag priority={priority} /> : null}
              {number ? (
                <Text as="span" variant="body" tone="muted" numeric>
                  #{number}
                </Text>
              ) : null}
            </Inline>
          ) : undefined
        }
        status={
          <StatusChip
            label={statusLabel ?? callStatusLabel(status)}
            tone={toneForStatus(status)}
          />
        }
        figure={
          weightClass ? (
            <Inline as="span" gap={2} align="end">
              {WEIGHT_WORD[weightClass]}
              <WeightClassBars weightClass={weightClass} />
            </Inline>
          ) : undefined
        }
        {...props}
      />
    );
  }
);
